import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { Leaf, Heart, Users, LifeBuoy, X, ChevronUp } from 'lucide-react';

const WellbeingBar = () => {
  const { isAuthenticated } = useAuth();
  const [expanded, setExpanded] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Don't show on public pages, module player (has its own bottom bar), or the wellbeing hub itself
  const hiddenPaths = ['/login', '/register', '/pricing', '/payment-success', '/wellbeing'];
  const isModulePage = /^\/(learn|training)\/[^/]+/.test(location.pathname);
  if (!isAuthenticated || hiddenPaths.includes(location.pathname) || isModulePage || location.pathname === '/') {
    return null;
  }

  const options = [
    {
      icon: Heart,
      title: 'Managing Stress & Pressure',
      description: 'Breathing techniques, grounding exercises, and emotional boundaries',
      color: 'soft-teal',
      section: 'stress',
    },
    {
      icon: Users,
      title: 'Who to Talk To at Work',
      description: 'Line managers, senior carers, safeguarding leads, and escalation pathways',
      color: 'lavender',
      section: 'talk',
    },
    {
      icon: LifeBuoy,
      title: 'Where to Seek Help as a Carer',
      description: 'Professional support, mental health resources, and peer networks',
      color: 'soft-teal',
      section: 'help',
    },
  ];

  return (
    <>
      {/* Backdrop dimmer when expanded. On mobile we deliberately stop short
          of the bottom 64px so the BottomNavigation tabs remain tappable
          (and the dimmer still acts as a click-to-dismiss for the rest of
          the screen). On desktop there is no bottom-nav so we cover the
          full viewport as before. */}
      {expanded && (
        <div
          className="fixed inset-x-0 top-0 bottom-16 xl:inset-0 bg-black/10 z-[998] wellbeing-overlay-enter"
          onClick={() => setExpanded(false)}
        />
      )}

      {/* Wellbeing Bar — sits above the mobile BottomNavigation (h-16) so
          the collapsed "Need support?" trigger never covers the carer's
          bottom-nav tabs. Desktop has no BottomNav so we anchor to 0. */}
      <div
        className={`fixed bottom-16 xl:bottom-0 left-0 right-0 z-[999] wellbeing-bar-transition ${
          expanded ? 'wellbeing-panel-expanded' : ''
        }`}
        data-testid="wellbeing-bar"
      >
        {/* Expanded Panel */}
        {expanded && (
          <div className="bg-white border-t border-warm-sand-200 shadow-2xl shadow-black/5 wellbeing-panel-enter">
            <div className="max-w-4xl mx-auto px-6 py-6">
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-2">
                  <Leaf className="w-5 h-5 text-soft-teal-500" />
                  <h3 className="text-warm-navy font-semibold font-[Poppins]">
                    Support & Wellbeing
                  </h3>
                </div>
                <button
                  onClick={() => setExpanded(false)}
                  className="p-1.5 rounded-full hover:bg-warm-sand-100 text-warm-navy-300 transition-colors"
                  data-testid="wellbeing-close-button"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="grid sm:grid-cols-3 gap-4">
                {options.map((opt, idx) => {
                  const Icon = opt.icon;
                  return (
                    <button
                      key={idx}
                      onClick={() => {
                        setExpanded(false);
                        navigate(`/wellbeing?section=${opt.section}`);
                      }}
                      className={`text-left p-5 rounded-2xl border transition-all duration-300 hover:shadow-md group
                        ${opt.color === 'lavender'
                          ? 'bg-lavender-50 border-lavender-100 hover:border-lavender-300'
                          : 'bg-soft-teal-50 border-soft-teal-100 hover:border-soft-teal-300'
                        }`}
                      style={{ animationDelay: `${idx * 80}ms` }}
                      data-testid={`wellbeing-option-${opt.section}`}
                    >
                      <div
                        className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3
                          ${opt.color === 'lavender' ? 'bg-lavender-200' : 'bg-soft-teal-100'}`}
                      >
                        <Icon
                          className={`w-5 h-5 ${
                            opt.color === 'lavender' ? 'text-lavender-500' : 'text-soft-teal-600'
                          }`}
                        />
                      </div>
                      <h4 className="text-warm-navy font-semibold text-sm mb-1 font-[Poppins]">
                        {opt.title}
                      </h4>
                      <p className="text-warm-navy-300 text-xs leading-relaxed">
                        {opt.description}
                      </p>
                    </button>
                  );
                })}
              </div>

              <div className="text-center mt-5">
                <button
                  onClick={() => {
                    setExpanded(false);
                    navigate('/wellbeing');
                  }}
                  className="text-soft-teal-600 text-sm font-medium hover:text-soft-teal-700 transition-colors"
                  data-testid="wellbeing-view-all"
                >
                  View full Wellbeing Hub
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Collapsed Bar */}
        {!expanded && (
          <div className="bg-white/95 backdrop-blur-sm border-t border-warm-sand-200">
            <div className="max-w-7xl mx-auto px-4">
              <button
                onClick={() => setExpanded(true)}
                className="w-full flex items-center justify-center gap-2 py-3 text-warm-navy-300 
                           hover:text-soft-teal-600 transition-colors duration-300 group"
                data-testid="wellbeing-bar-trigger"
              >
                <Leaf className="w-4 h-4 text-soft-teal-500 group-hover:text-soft-teal-600 transition-colors" />
                <span className="text-sm font-medium">Need support?</span>
                <ChevronUp className="w-3.5 h-3.5 opacity-50 group-hover:opacity-100 transition-opacity" />
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default WellbeingBar;
